import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetEngageJourneys } from '../../Hooks/useJourney';
import { EngageObject } from '../../Types/engage';
import { Journey } from '../../Types/journey';
import './JourneySidePanel.scss';
import { getEngageObjectTypeIcon } from '../../Services/engage';
import { BizIcon } from '../Mini/BizIcon';
import { isCurrentItem, resolveCurrentItemId, resolveCurrentObjectType, resolveJourney, JourneyLookupItem } from './journeyLookup';

type JourneySidePanelItem = JourneyLookupItem;

type JourneySidePanelProps = {
	item?: JourneySidePanelItem;
	typeName?: string;
	initiallyExpanded?: boolean;
	className?: string;
	goToEngageItemLegacyFn?: (item: EngageObject) => void;
};

const JourneySidePanel = ({
	item,
	typeName,
	initiallyExpanded = true,
	className = '',
	goToEngageItemLegacyFn,
}: JourneySidePanelProps) => {
	const { t } = useTranslation();
	const { journeys, statusState } = useGetEngageJourneys();
	const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

	const currentObjectType = useMemo(() => resolveCurrentObjectType(item, typeName), [item, typeName]);
	const currentItemId = useMemo(() => resolveCurrentItemId(item), [item]);
	
	const selectedJourney = useMemo(() => resolveJourney(journeys, currentItemId, currentObjectType), [journeys, currentItemId, currentObjectType]);
	
	useEffect(() => {
		// set .sidbar-open class on body when panel is expanded to allow custom styling based on this state
		if (isExpanded && selectedJourney) {
			document.body.classList.add('sidebar-open');
		} else {
			document.body.classList.remove('sidebar-open');
		}
		// cleanup on unmount
		return () => {
			document.body.classList.remove('sidebar-open');
		};
	}, [isExpanded, selectedJourney]);

	const handleGoToEngageItem = (journeyItem: EngageObject) => {
		if (!goToEngageItemLegacyFn) return;
		goToEngageItemLegacyFn(journeyItem);
	};

	return (
		<div className={`biz-journey-side-panel ${isExpanded ? 'biz-journey-side-panel--expanded' : ''} ${className}`.trim()}>
			{!isExpanded && (
				<button
					type='button'
					className='biz-journey-side-panel__toggle-collapsed'
					onClick={() => setIsExpanded(true)}
					title={t('JOURNEY.SHOW_JOURNEY', 'Show journey')}
				>
					<i className='fas fa-route' aria-hidden='true'></i>
				</button>
			)}

			{isExpanded && selectedJourney && (
				<div className='biz-journey-side-panel__panel'>
					{statusState === 'LOADING' && (
						<div className='biz-journey-side-panel__state text-muted'>
							<BizIcon icon={'LOADING'} />
						</div>
					)}

					{/* {statusState !== 'LOADING' && !selectedJourney && (
						<div className='biz-journey-side-panel__state text-muted'>
							{t('JOURNEY.NO_CONTENT', 'No journey content found')}
						</div>
					)} */}

					{selectedJourney && (
						<>
							<div className='biz-journey-side-panel__header'>
								<button
									type='button'
									className='biz-journey-side-panel__toggle-expand'
									onClick={() => setIsExpanded(false)}
									title={t('JOURNEY.HIDE', 'Hide')}
								>
									<i className='fas fa-angles-left' aria-hidden='true'></i>
								</button>
								<h4 className='biz-journey-side-panel__title'>{selectedJourney.Name}</h4>
								<JourneyProgressCircle journey={selectedJourney} />
							</div>

							<div className='biz-journey-side-panel__list' role='list'>
								{(selectedJourney.EngageObjects || []).map((journeyObject, index) => {
									const current = isCurrentItem(journeyObject, currentItemId, currentObjectType);
									const statusIcon = getStatusIcon(journeyObject.Status, t);
									const itemLabel = journeyObject.Name || t('JOURNEY.ITEM', 'Item');

									return (
										<button
											type='button'
											className={`biz-journey-side-panel__item ${current ? 'biz-journey-side-panel__item--current' : ''}`.trim()}
											onClick={() => handleGoToEngageItem(journeyObject)}
											key={`${journeyObject.Id}-${journeyObject.EngageObjectType}-${index}`}
											role='listitem'
										>
											<div className='biz-journey-side-panel__item-main'>
												<span className='biz-journey-side-panel__item-order'>{index + 1}</span>
												<i
													className={`${getEngageObjectTypeIcon(journeyObject.EngageObjectType)} biz-journey-side-panel__item-icon`}
													aria-hidden='true'
												></i>
												<span className='biz-journey-side-panel__item-name line-limit lines-2'>
													{itemLabel}
												</span>
											</div>
											<span className='biz-journey-side-panel__status-icon-wrapper'>
												{statusIcon && (
													<i
														className={`${statusIcon.iconClassName} ${statusIcon.colorClassName} biz-journey-side-panel__status-icon`}
														title={statusIcon.title}
														aria-label={statusIcon.title}
													></i>
												)}
											</span>
										</button>
									);
								})}
							</div>
						</>
					)}
				</div>
			)}
		</div>
	);
};

const CIRCLE_SIZE = 32;
const CIRCLE_STROKE = 2;
const CIRCLE_RADIUS = (CIRCLE_SIZE - CIRCLE_STROKE) / 2;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

const JourneyProgressCircle = ({ journey }: { journey: Journey }) => {
	const percent = Math.round(journey.TotalProgress || 0);
	const completed = (journey.EngageObjects || []).filter((o) => o.Status === 3).length;
	const total = (journey.EngageObjects || []).length;
	const offset = CIRCLE_CIRCUMFERENCE - (percent / 100) * CIRCLE_CIRCUMFERENCE;

	return (
		<div className='biz-journey-side-panel__progress'>
			<span className='biz-journey-side-panel__progress-count'>{completed}/{total}</span>
			<div className='biz-journey-side-panel__progress-circle'>
				<svg width={CIRCLE_SIZE} height={CIRCLE_SIZE} viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}>
					<circle
						className='biz-journey-side-panel__progress-track'
						cx={CIRCLE_SIZE / 2}
						cy={CIRCLE_SIZE / 2}
						r={CIRCLE_RADIUS}
						strokeWidth={CIRCLE_STROKE}
					/>
					<circle
						className='biz-journey-side-panel__progress-fill'
						cx={CIRCLE_SIZE / 2}
						cy={CIRCLE_SIZE / 2}
						r={CIRCLE_RADIUS}
						strokeWidth={CIRCLE_STROKE}
						strokeDasharray={CIRCLE_CIRCUMFERENCE}
						strokeDashoffset={offset}
					/>
				</svg>
				<span className='biz-journey-side-panel__progress-label'>{percent}%</span>
			</div>
		</div>
	);
};

function getStatusIcon(
	status: number | undefined,
	t: (key: string, fallback: string) => string
): { iconClassName: string; colorClassName: string; title: string } | undefined {
	if (status === 3) {
		return {
			iconClassName: 'fas fa-check-circle',
			colorClassName: 'text-success',
			title: t('JOURNEY.STATUS_COMPLETED', 'Completed'),
		};
	}

	if (status === 2) {
		return {
			iconClassName: 'fas fa-circle-xmark',
			colorClassName: 'text-danger',
			title: t('JOURNEY.STATUS_FAILED', 'Failed'),
		};
	}

	if ((status ?? 0) > 0) {
		return {
			iconClassName: 'far fa-clock',
			colorClassName: 'text-warning',
			title: t('JOURNEY.STATUS_STARTED', 'Started'),
		};
	}

	return undefined;
}

export default JourneySidePanel;
